import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { useNetsuiteVendorListLazyQuery, useNetsuiteVendorNameLazyQuery } from '@anytag/graphql';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  NetsuiteVendorSearchOption,
  AsyncSearchNetsuiteVendorFieldProps,
  SingleAsyncSearchNetsuiteVendorFieldProps,
  MultipleAsyncSearchNetsuiteVendorFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchNetsuiteVendorField<T extends string>(
  props: MultipleAsyncSearchNetsuiteVendorFieldProps<T>
): React.ReactElement;
export function AsyncSearchNetsuiteVendorField<T extends string>(
  props: SingleAsyncSearchNetsuiteVendorFieldProps<T>
): React.ReactElement;
export function AsyncSearchNetsuiteVendorField<T extends string>({
  name,
  title,
  multiple,
  onChange,
  searchProps,
  ...restProps
}: AsyncSearchNetsuiteVendorFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getNetsuiteVendorList, loading: fetchingList } = useNetsuiteVendorListLazyQuery();
  const { getNetsuiteVendorName, loading: fetchingName } = useNetsuiteVendorNameLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<NetsuiteVendorSearchOption> }>();

  const value = watch(name);
  const selectedVendors = getValuesArray({ value, multiple }) as string[];

  const fetchNetsuiteVendors: AsyncSelectorSearchFieldProps<T, NetsuiteVendorSearchOption>['onSearch'] = async (
    keyword,
    initialLoad
  ) => {
    let vendors: NetsuiteVendorSearchOption[] = [];

    try {
      if (initialLoad) {
        // TODO: ask BE - should implement like other async search fields
        const results = await Promise.all(
          selectedVendors.map((id) => getNetsuiteVendorName({ variables: { netsuiteId: id } }))
        );
        vendors = results
          .flatMap((result) => (result.data?.netsuiteVendorName ? [result.data.netsuiteVendorName] : []))
          .map((vendor) => ({ value: vendor.id, label: vendor.name, ...vendor }));
      } else {
        const { data } = await getNetsuiteVendorList({ variables: { keyword: keyword ?? '' } });
        vendors =
          data?.netsuiteVendorList?.map((vendor) => ({ value: vendor.id, label: vendor.name, ...vendor })) || [];
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return vendors;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, NetsuiteVendorSearchOption>,
    'name' | 'loading' | 'onSearch' | 'title' | 'searchProps'
  > = {
    name,
    onSearch: fetchNetsuiteVendors,
    loading: fetchingList || fetchingName,
    title: title ?? t<string>('Selector.NetSuite Vendor'),
    searchProps: { placeholder: t<string>('Search by Vendor name'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, NetsuiteVendorSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchNetsuiteVendorFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, NetsuiteVendorSearchOption>
      {...(restProps as Partial<SingleAsyncSearchNetsuiteVendorFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
